import {
  Attachment,
  AttachmentVisibility,
  User as CoreUser,
} from '@policyfly/protobuf'
import {
  CreateCommentRequest,
  CreateCommentResponse,
  DeleteCommentRequest,
  DeleteCommentResponse,
  MarkCommentReadRequest,
  MarkCommentReadResponse,
  PatrickServiceClient,
  ReadCommentRequest,
  ReadCommentResponse,
  UpdateCommentVisibilityRequest,
  UpdateCommentVisibilityResponse,
} from '@policyfly/protobuf/patrick'
import { FeedItemVisibility } from '@policyfly/protobuf/pfactivity'

import { usePermission } from '@/composables/permission'
import { useAuthenticationStore } from '@/stores/authentication'

import { api } from '@/api'
import { devtools } from '@/plugins/devtools/api'
import { coreUserToActivityUser, patrickUserToActivityUser, patrickUserToMention } from '@/utils/protobuf'
import { activityFeedItemToFeedEventLog } from '@/utils/protobuf/mappers/feed'

import type { ApiVariableEndpoints, CreateEndpointParams } from '@/stores/api'
import type { User } from '@policyfly/protobuf/patrick'
import type { ActivityFeedItem } from '@policyfly/protobuf/pfactivity'
import type { FeedEventLog } from 'types/events'
import type { UnifiedMembershipUser } from 'types/user'

export interface CommentResponse {
  item?: ActivityFeedItem
  json: FeedEventLog
}

export interface CommentApiEndpoints {
  /**
   * Add a new comment to an application or policy.
   */
  create: (params: {
    applicationId?: string
    policyId?: string
    comment: string
    visibility?: FeedItemVisibility
    files?: File[]
    mentions?: User[]
  }) => Promise<CommentResponse>
  /**
   * Soft delete a comment.
   */
  delete: (id: string) => Promise<void>
  /**
   * Fetch a single comment.
   */
  read: (id: string) => Promise<CommentResponse>
  /**
   * Update the visibility of a comment.
   */
  updateVisibility: (id: string, visibility: FeedItemVisibility) => Promise<CommentResponse>
  /**
   * Mark a single comment as read.
   */
  markRead: (id: string) => Promise<CommentResponse>
}

/**
 * Creates endpoints related to Comments.
 */
export const createCommentEndpoints = (params: CreateEndpointParams): ApiVariableEndpoints<CommentApiEndpoints> => {
  const patrickServiceClient = new PatrickServiceClient(params.transport)
  const authenticationStore = useAuthenticationStore()

  const { checkPermission } = usePermission()

  const createDjango: CommentApiEndpoints['create'] = async ({
    applicationId,
    policyId,
    comment: body,
    visibility = FeedItemVisibility.FEED_ITEM_VISIBILITY_STANDARD,
    files,
    mentions,
  }) => {
    const comment = {
      ...visibility === FeedItemVisibility.FEED_ITEM_VISIBILITY_RESTRICTED ? { restricted_visibility: true } : {},
      ...(mentions && mentions.length) ? { mentioned_users: mentions.map(patrickUserToMention) } : {},
    }

    if (files?.length) {
      const payload = { ...comment, application: applicationId, comment_body: body, file: files[0] }
      const formData = new FormData()
      Object.entries(payload)
        .forEach(([key, value]: [string, string | number | boolean | File | Pick<UnifiedMembershipUser, 'email' | 'first_name' | 'last_name'>[] | undefined]) => {
          if (Array.isArray(value)) formData.append(key, JSON.stringify(value))
          else if (value !== undefined) formData.append(key, key === 'file' ? value as File : String(value))
        })

      const { data } = await api.applications.upload({ body: formData, query: {} })
      return {
        json: data.EventLog!,
      }
    }

    const { data } = await api.applications.comment({ path: { pk: +(applicationId ?? +policyId!) }, body: { ...comment, body } })
    return {
      json: data.Application.events[0]!,
    }
  }
  const createGrpc: CommentApiEndpoints['create'] = async ({
    applicationId,
    policyId,
    comment: content,
    visibility = FeedItemVisibility.FEED_ITEM_VISIBILITY_STANDARD,
    files,
    mentions,
  }) => {
    const author = CoreUser.clone(authenticationStore.coreUser)
    const attachments = []
    for (const file of files ?? []) {
      const attachment = Attachment.create({
        data: new Uint8Array(await file.arrayBuffer()),
        metadata: {
          applicationIds: applicationId ? [applicationId] : [],
          policyId,
          programId: String(authenticationStore.programIds.id),
          name: file.name,
          mimeType: file.type,
          sizeBytes: String(file.size),
          visibility: visibility === FeedItemVisibility.FEED_ITEM_VISIBILITY_RESTRICTED
            ? AttachmentVisibility.Visibility_Restricted
            : undefined,
          owner: author,
        },
      })
      attachments.push(attachment)
    }

    const requestAuthor = coreUserToActivityUser(author)
    const request = CreateCommentRequest.create({
      comment: {
        applicationId,
        policyId,
        visibility,
        item: {
          oneofKind: 'comment',
          comment: {
            attachments,
            author: requestAuthor,
            content,
            mentions: mentions?.map(patrickUserToActivityUser) ?? [],
            readable: !checkPermission('commentInternal'),
            systemGenerated: false,
          },
        },
      },
    })
    const { response } = await patrickServiceClient.createComment(request)

    devtools.logGrpc({
      description: 'Create Comment',
      messages: [
        { type: CreateCommentRequest, key: 'request', message: request },
        { type: CreateCommentResponse, key: 'response', message: response },
      ],
    })

    return {
      item: response.comment,
      json: activityFeedItemToFeedEventLog(response.comment!)!,
    }
  }

  const deleteDjango: CommentApiEndpoints['delete'] = async () => {
    throw new Error('Django programs do not support deleting comments')
  }
  const deleteGrpc: CommentApiEndpoints['delete'] = async (id) => {
    const request = DeleteCommentRequest.create({ id, soft: true })
    const { response } = await patrickServiceClient.deleteComment(request)

    devtools.logGrpc({
      description: 'Delete Comment',
      messages: [
        { type: DeleteCommentRequest, key: 'request', message: request },
        { type: DeleteCommentResponse, key: 'response', message: response },
      ],
    })
  }

  const readDjango: CommentApiEndpoints['read'] = async () => {
    throw new Error('Django programs do not support reading comments')
  }
  const readGrpc: CommentApiEndpoints['read'] = async (id) => {
    const request = ReadCommentRequest.create({ id })
    const { response } = await patrickServiceClient.readComment(request)

    devtools.logGrpc({
      description: 'Read Comment',
      messages: [
        { type: ReadCommentRequest, key: 'request', message: request },
        { type: ReadCommentResponse, key: 'response', message: response },
      ],
    })

    return {
      item: response.comment,
      json: activityFeedItemToFeedEventLog(response.comment!)!,
    }
  }

  const updateVisibilityDjango: CommentApiEndpoints['updateVisibility'] = async () => {
    throw new Error('Django programs do not support editing comments')
  }
  const updateVisibilityGrpc: CommentApiEndpoints['updateVisibility'] = async (id, visibility) => {
    const request = UpdateCommentVisibilityRequest.create({ id, visibility })
    const { response } = await patrickServiceClient.updateCommentVisibility(request)

    devtools.logGrpc({
      description: 'Update Comment Visibility',
      messages: [
        { type: UpdateCommentVisibilityRequest, key: 'request', message: request },
        { type: UpdateCommentVisibilityResponse, key: 'response', message: response },
      ],
    })

    return {
      item: response.comment,
      json: activityFeedItemToFeedEventLog(response.comment!)!,
    }
  }

  const markReadDjango: CommentApiEndpoints['markRead'] = async (id) => {
    const { data } = await api.comments.mark_read({ path: { pk: +id } })

    return {
      json: data.EventLog,
    }
  }
  const markReadGrpc: CommentApiEndpoints['markRead'] = async (id) => {
    const request = MarkCommentReadRequest.create({ id, user: CoreUser.clone(authenticationStore.coreUser) })
    const { response } = await patrickServiceClient.markCommentRead(request)

    devtools.logGrpc({
      description: 'Mark Comment Read',
      messages: [
        { type: MarkCommentReadRequest, key: 'request', message: request },
        { type: MarkCommentReadResponse, key: 'response', message: response },
      ],
    })

    return {
      item: response.comment,
      json: activityFeedItemToFeedEventLog(response.comment!)!,
    }
  }

  return {
    django: {
      create: createDjango,
      delete: deleteDjango,
      read: readDjango,
      updateVisibility: updateVisibilityDjango,
      markRead: markReadDjango,
    },
    grpc: {
      create: createGrpc,
      delete: deleteGrpc,
      read: readGrpc,
      updateVisibility: updateVisibilityGrpc,
      markRead: markReadGrpc,
    },
  }
}
